'use client'

import { trackMarketingDemoInteracted } from '@/lib/analytics'
import { Fragment, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Eye, Lock, Volume2 } from '@/components/ui/icons'
import { poster, Still } from './ProductionShared'

// A replica of the editor's timeline: ruler, V/A track headers, thumbnail clips, playhead,
// and three remote collaborators (the same coloured line + chip the real RemoteIndicators use).
// It is a real (toy) editor: scrub the ruler, drag / trim / re-track clips, Space to play.
//
// Performance model: React state is the committed layout only. Every per-frame update writes
// styles straight to the DOM — the user's drag/trim/scrub synchronously from the pointer event,
// the collaborators' cursors and edits from one rAF loop — and commits to state on pointerup /
// when a collaborator lets go.
// Collaborators run a seeded pseudo-random script started in an effect, so SSR and the first
// client render agree and reduced-motion users get a still timeline that still edits.
// `compact` (the homepage hero) drops the sequence bar and the second audio track; `ref` lets the
// hero's canvas story drop a shot into the sequence and take it back when its loop restarts.

const HEADER_W = 80
const DURATION = 30 // seconds on the ruler
const FPS = 24
const RULER_H = 28
const TRACK_H = 46
const GAP_H = 6 // divider between the video and audio tracks
const MIN_W = 3 // % of the sequence, ~0.9s
const SNAP_PX = 9 // magnetic radius for the user's drags
const SNAP_MAX_V = 0.6 // px/ms; faster than this the drag is a throw, not a placement, so no snap
const SNAP_PCT = 1.2 // the collaborators' radius, in % (≈ SNAP_PX on a 720px lane)
const NUDGE = 0.5 // % per arrow key

// Collaborator colours come from the dashboard series palette so they match the usage row.
// `home` is the stretch of the sequence each one works in, `calm` scales their pauses, `pace`
// is ms per % of travel, `edits` the odds a beat turns into a clip edit.
export const PEOPLE = {
  director: { name: 'Director', color: '#f5a623', top: 132, x: 12, home: [4, 48], calm: 1.6, pace: 26, edits: 0.18 },
  editor: { name: 'Editor', color: '#e85d75', top: 86, x: 72, home: [28, 92], calm: 1, pace: 16, edits: 0.4 },
  claude: { name: 'Claude Code', color: '#5b9ef5', top: 40, x: 96, home: [45, 100], calm: 0.45, pace: 7, edits: 0.35 },
} as const
type Who = keyof typeof PEOPLE
const WHO = Object.keys(PEOPLE) as Who[]

type TrackId = 'V2' | 'V1' | 'A1' | 'A2'
export type TimelineClip = {
  id: string
  track: TrackId
  l: number
  w: number
  src?: string
  pos?: string
  label: string
  /** An editor's colour label: a strip on the clip, and the tint of its landing ring. */
  tag?: string
}
type Clip = TimelineClip
export type TimelineHandle = { drop: (clip: Clip) => void; remove: (id: string) => void }

const TRACKS: { id: TrackId; kind: 'video' | 'audio' }[] = [
  { id: 'V2', kind: 'video' },
  { id: 'V1', kind: 'video' },
  { id: 'A1', kind: 'audio' },
  { id: 'A2', kind: 'audio' },
]
const ROW: Record<TrackId, number> = { V2: 0, V1: 1, A1: 2, A2: 3 }
const trackTop = (t: TrackId) => RULER_H + ROW[t] * TRACK_H + (ROW[t] >= 2 ? GAP_H : 0)
const TICKS = Array.from({ length: DURATION + 1 }, (_, s) => (s / DURATION) * 100)

// Positions are % of the sequence, one decimal, so SSR and client emit identical markup.
// V1 keeps a few gaps and a free tail so the collaborators have somewhere to move things.
const INITIAL: Clip[] = [
  { id: 'title', track: 'V2', l: 8, w: 12, src: poster(28), label: 'Title card' },
  { id: 's07', track: 'V2', l: 52, w: 14, src: poster(49), label: 'Shot 07 · take 3' },
  { id: 's06', track: 'V2', l: 82, w: 15, src: poster(51), label: 'Shot 06 · alt' },
  { id: 's01', track: 'V1', l: 0, w: 14, src: poster(8), label: 'Shot 01' },
  { id: 's02', track: 'V1', l: 15, w: 16, src: poster(16), label: 'Shot 02' },
  { id: 's03', track: 'V1', l: 33, w: 16, src: poster(19), label: 'Shot 03' },
  { id: 's04', track: 'V1', l: 50, w: 20, src: poster(42), label: 'Shot 04' },
  { id: 's05', track: 'V1', l: 72, w: 16, src: poster(21), label: 'Shot 05' },
  { id: 'vo', track: 'A1', l: 0, w: 62, label: 'vo_take2.wav' },
  { id: 'score', track: 'A2', l: 14, w: 83, label: 'score_v1.mp3' },
]

// A waveform that reads as recorded audio: phrases with gaps between them, a fast attack and a
// longer decay on each, fine sample-to-sample variation, mirrored about the centre line.
// Deterministic (seeded) and rounded, so server and client emit identical markup.
const WAVE_D = (() => {
  let seed = 7
  const rand = () => (seed = (seed * 1664525 + 1013904223) % 4294967296) / 4294967296
  const N = 360
  const amp: number[] = []
  while (amp.length < N) {
    const len = 14 + Math.floor(rand() * 44) // one phrase
    const peak = 0.45 + rand() * 0.55
    for (let i = 0; i < len && amp.length < N; i++) {
      const t = i / len
      const env = Math.min(1, t / 0.08) * Math.pow(1 - t, 0.6) // quick in, slow out
      amp.push(peak * env * (0.35 + 0.65 * rand()))
    }
    for (let i = 3 + Math.floor(rand() * 9); i > 0 && amp.length < N; i--) amp.push(0.02 + rand() * 0.03) // breath
  }
  const top = amp.map((a, i) => `${i ? 'L' : 'M'}${i} ${(5 - a * 4.4).toFixed(2)}`).join('')
  const bottom = amp.map((a, i) => `L${N - 1 - i} ${(5 + amp[N - 1 - i] * 4.4).toFixed(2)}`).join('')
  return { d: `${top}${bottom}Z`, n: N }
})()
// One constant element: React skips reconciling it on every commit.
const WAVE_SVG = (
  <svg
    className="absolute inset-0 h-full w-full"
    viewBox={`0 0 ${WAVE_D.n - 1} 10`}
    preserveAspectRatio="none"
    aria-hidden
  >
    <line
      x1="0"
      y1="5"
      x2={WAVE_D.n - 1}
      y2="5"
      stroke="rgba(134,239,172,0.35)"
      strokeWidth="0.15"
      vectorEffect="non-scaling-stroke"
    />
    <path d={WAVE_D.d} fill="rgba(134,239,172,0.8)" />
  </svg>
)

const CSS = `
@media (prefers-reduced-motion: no-preference) {
  .prod-tl-playhead { animation: prod-tl-play 16s linear infinite; }
}
@keyframes prod-tl-play { from { left: 4%; } to { left: 46%; } }
`
const PULSE: Keyframe[] = [
  { transform: 'scaleX(5)', opacity: 1 },
  { transform: 'scaleX(1)', opacity: 1 },
]

const round1 = (n: number) => Math.round(n * 10) / 10
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))
const easeOut = (k: number) => 1 - (1 - k) ** 3

// Nearest snap target within `tol`, or null.
function nearest(x: number, targets: number[], tol: number) {
  let best: number | null = null
  let dist = tol
  for (const t of targets) {
    const d = Math.abs(t - x)
    if (d <= dist) {
      dist = d
      best = t
    }
  }
  return best
}

// 00:07:12 — minutes:seconds:frames at 24 fps, from a % of the sequence.
function timecode(pct: number) {
  const frames = Math.round((pct / 100) * DURATION * FPS)
  const s = Math.floor(frames / FPS)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}:${pad(frames % FPS)}`
}

const overlaps = (clips: Clip[], c: Clip) =>
  clips.some(o => o.id !== c.id && o.track === c.track && o.l < c.l + c.w && c.l < o.l + o.w)

// Drop `next` into its track and push the neighbours out of the way (each toward the side its
// centre is on, chained); if a chain runs off either end the moved clip gives way instead.
// null = the track cannot hold it.
// ponytail: preserved gaps can make a 3rd pass fail on an over-full track; that is a refusal, not a crash.
function place(clips: Clip[], next: Clip): Clip[] | null {
  const mid = next.l + next.w / 2
  const same = clips.filter(c => c.id !== next.id && c.track === next.track).sort((a, b) => a.l - b.l)
  let l = clamp(next.l, 0, 100 - next.w)
  for (let pass = 0; pass < 3; pass++) {
    const moved = new Map<string, number>()
    let cursor = l + next.w
    for (const c of same) {
      if (c.l + c.w / 2 < mid) continue
      const nl = Math.max(c.l, cursor)
      moved.set(c.id, nl)
      cursor = nl + c.w
    }
    const over = cursor - 100
    cursor = l
    for (let i = same.length - 1; i >= 0; i--) {
      const c = same[i]
      if (c.l + c.w / 2 >= mid) continue
      const nl = Math.min(c.l, cursor - c.w)
      moved.set(c.id, nl)
      cursor = nl
    }
    const under = -cursor
    if (over <= 0 && under <= 0) {
      return clips.map(c => {
        if (c.id === next.id) return { ...next, l: round1(l) }
        const nl = moved.get(c.id)
        return nl === undefined || nl === c.l ? c : { ...c, l: round1(nl) }
      })
    }
    if (over > 0 && under > 0) return null
    l = over > 0 ? l - over : l + under
  }
  return null
}

// Direct style writes for anything mid-gesture; React re-applies the committed values later.
function paint(el: HTMLElement, c: Clip, dy = 0, lift = false) {
  const s = el.style
  s.left = `${c.l}%`
  s.width = `${c.w}%`
  s.transform = dy ? `translate3d(0,${dy}px,0)` : ''
  s.zIndex = lift ? '10' : ''
}

// Snap guide: a bright vertical line at the snap point that pulses each time the point changes.
function guide(el: HTMLElement, at: number | null) {
  if (at === null) {
    el.style.opacity = '0'
    el.dataset.at = ''
    return
  }
  const key = at.toFixed(2)
  el.style.left = `${at}%`
  el.style.opacity = '1'
  if (el.dataset.at !== key) {
    el.dataset.at = key
    el.animate(PULSE, { duration: 200, easing: 'ease-out' })
  }
}

// Seeded PRNG so every collaborator runs the same script on every load.
function mulberry32(a: number) {
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

type ClipProps = { clip: Clip; selected: boolean; onFocus: (e: React.FocusEvent<HTMLButtonElement>) => void }

function trackLabel(clip: Clip) {
  const end = clip.l + clip.w
  return `${clip.label}, ${clip.track}, ${timecode(clip.l)} to ${timecode(end)}`
}

// Trim handles + selection ring + "You" chip, shared by video and audio clips.
function ClipChrome({ selected }: { selected: boolean }) {
  return (
    <>
      <div data-handle="l" className="absolute inset-y-0 left-0 w-2 cursor-ew-resize" />
      <div data-handle="r" className="absolute inset-y-0 right-0 w-2 cursor-ew-resize" />
      {selected && (
        <div className="pointer-events-none absolute inset-0 rounded ring-1 ring-inset ring-white">
          <span className="absolute right-1 top-1 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium leading-none text-black">
            You
          </span>
        </div>
      )}
    </>
  )
}

const CLIP_CLASS =
  'absolute inset-y-0 cursor-grab touch-pan-y overflow-hidden rounded border-b border-l border-t border-[#121212] text-left outline-none focus-visible:ring-1 focus-visible:ring-white/60'

// globals.css transitions `all` on every button (unlayered, so no utility class overrides it): the
// clip would ease 200ms behind the pointer. Inline `transition: none` is load-bearing.
const CLIP_STYLE = (clip: Clip): React.CSSProperties => ({
  left: `${clip.l}%`,
  width: `${clip.w}%`,
  transition: 'none',
})

const VideoClip = memo(function VideoClip({ clip, selected, onFocus }: ClipProps) {
  return (
    <button
      type="button"
      data-clip={clip.id}
      aria-label={trackLabel(clip)}
      aria-pressed={selected}
      onFocus={onFocus}
      className={CLIP_CLASS}
      style={CLIP_STYLE(clip)}
    >
      <div className="absolute inset-0 bg-blue-500/25" />
      {clip.tag && <span className="absolute inset-y-0 left-0 w-[3px]" style={{ backgroundColor: clip.tag }} />}
      <div className="absolute inset-0 flex items-center gap-1 px-1">
        <div className="h-[28px] w-[50px] shrink-0 overflow-hidden rounded-[2px] bg-neutral-900">
          {clip.src && <Still src={clip.src} pos={clip.pos} className="h-full w-full" />}
        </div>
        <span className="truncate text-[10px] font-medium text-neutral-400">{clip.label}</span>
      </div>
      <ClipChrome selected={selected} />
    </button>
  )
})

const AudioClip = memo(function AudioClip({ clip, selected, onFocus }: ClipProps) {
  return (
    <button
      type="button"
      data-clip={clip.id}
      aria-label={trackLabel(clip)}
      aria-pressed={selected}
      onFocus={onFocus}
      className={CLIP_CLASS}
      style={CLIP_STYLE(clip)}
    >
      <div className="absolute inset-0 bg-[#12281c]" />
      {WAVE_SVG}
      <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-[10px] font-medium text-neutral-200">
        {clip.label}
      </span>
      <ClipChrome selected={selected} />
    </button>
  )
})

function Track({ label, kind, children }: { label: string; kind: 'video' | 'audio'; children: React.ReactNode }) {
  const Icon = kind === 'video' ? Eye : Volume2
  return (
    <div className="flex border-b border-[#333]" style={{ height: TRACK_H }}>
      <div
        className="flex shrink-0 items-center gap-2 border-r border-[#333] bg-[#1f1f1f] px-2.5"
        style={{ width: HEADER_W }}
      >
        <span className="w-5 text-[11px] font-medium tabular-nums text-[#a3a3a3]">{label}</span>
        <Icon className="h-3.5 w-3.5 text-[#a3a3a3]" />
        <Lock className="h-3.5 w-3.5 text-[#a3a3a3] opacity-40" />
      </div>
      <div className="relative min-w-0 flex-1 bg-[#161616]">{children}</div>
    </div>
  )
}

type Drag =
  | { kind: 'scrub' }
  | {
      kind: 'move' | 'trimL' | 'trimR'
      id: string
      orig: Clip
      x0: number
      tol: number // snap radius in % for this drag
      last: { x: number; t: number } // previous move, for pointer velocity
      els: Map<string, HTMLElement> // every element this drag has painted
      next: Clip | null
    }

// `initial` replaces the sequence (the hero's film), `ambient={false}` leaves the collaborators
// out (the hero tells its own story on the canvas), `colors` recolours the three of them.
export function ProductionTimeline({
  compact = false,
  ref,
  initial,
  ambient = true,
  colors,
  parked,
}: {
  compact?: boolean
  ref?: React.Ref<TimelineHandle>
  initial?: Clip[]
  ambient?: boolean
  colors?: Record<Who, string>
  /** A collaborator sitting on a clip, static: a ring in their colour and their name. */
  parked?: { clip: string; name: string; color: string }
}) {
  const ticks = Array.from({ length: DURATION }, (_, s) => s)
  const tracks = compact ? TRACKS.filter(t => t.id !== 'A2') : TRACKS
  const base = initial ?? INITIAL
  const [clips, setClips] = useState(() => (compact ? base.filter(c => c.track !== 'A2') : base))
  const baseRef = useRef(base)
  const color = (who: Who) => colors?.[who] ?? PEOPLE[who].color
  const [playhead, setPlayhead] = useState(4)
  const [selected, setSelected] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [scrubbing, setScrubbing] = useState(false)
  const [interacted, setInteracted] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const playheadRef = useRef<HTMLDivElement>(null)
  const userGuideRef = useRef<HTMLDivElement>(null)
  const tcRef = useRef<HTMLSpanElement>(null)
  const chipRef = useRef<HTMLSpanElement>(null)
  // Latest committed state for the rAF loops, which must not close over a render.
  const clipsRef = useRef(clips)
  const selRef = useRef(selected)
  const phRef = useRef(4) // live playhead %, ahead of the committed `playhead`
  const interactedRef = useRef(false)
  const drag = useRef<Drag | null>(null)
  const geo = useRef<{ left: number; top: number; laneW: number } | null>(null)
  // Set by the ambient effect: make a collaborator let go of a clip the user just grabbed or pushed.
  const abortHolder = useRef<((id: string) => void) | null>(null)
  useEffect(() => {
    clipsRef.current = clips
    selRef.current = selected
  })

  useImperativeHandle(ref, () => ({
    drop: clip => {
      setClips(cs => (cs.some(c => c.id === clip.id) ? cs : (place([...cs, clip], clip) ?? cs)))
      // Land the new clip once React has placed it (a discrete update, so it is in the DOM next
      // frame): a short drop-in and a ring that fades, so the eye catches where it went.
      requestAnimationFrame(() => {
        bodyRef.current?.querySelector(`[data-clip="${clip.id}"]`)?.animate(
          [
            { opacity: 0, transform: 'translateY(-6px)', boxShadow: `0 0 0 2px ${clip.tag ?? '#fff'}` },
            { opacity: 1, transform: 'none', boxShadow: `0 0 0 2px ${clip.tag ?? '#fff'}`, offset: 0.3 },
            { opacity: 1, transform: 'none', boxShadow: '0 0 0 2px rgba(255,255,255,0)' },
          ],
          { duration: 1100, easing: 'ease-out' },
        )
      })
    },
    remove: id => {
      abortHolder.current?.(id)
      setClips(cs => (cs.some(c => c.id === id) ? cs.filter(c => c.id !== id) : cs))
    },
  }))

  const setPlayheadDom = useCallback((x: number) => {
    phRef.current = x
    if (playheadRef.current) playheadRef.current.style.left = `${x}%`
    const tc = timecode(x)
    if (tcRef.current) tcRef.current.textContent = tc
    if (chipRef.current) chipRef.current.textContent = tc
  }, [])

  // First user input: take the playhead over where the ambient animation left it (no jump).
  const takeOver = () => {
    if (interactedRef.current) return
    interactedRef.current = true
    const ph = playheadRef.current!
    phRef.current = round1((ph.offsetLeft / ph.parentElement!.clientWidth) * 100)
    setPlayhead(phRef.current)
    setInteracted(true)
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    const target = e.target as HTMLElement
    const clipEl = target.closest<HTMLElement>('[data-clip]')
    // The one layout read of the gesture.
    const r = bodyRef.current!.getBoundingClientRect()
    geo.current = { left: r.left + HEADER_W, top: r.top, laneW: r.width - HEADER_W }
    takeOver()
    if (clipEl) {
      const id = clipEl.dataset.clip!
      abortHolder.current?.(id)
      const orig = clips.find(c => c.id === id)!
      const handle = target.closest<HTMLElement>('[data-handle]')?.dataset.handle
      drag.current = {
        kind: handle === 'l' ? 'trimL' : handle === 'r' ? 'trimR' : 'move',
        id,
        orig,
        x0: e.clientX,
        tol: (SNAP_PX / geo.current.laneW) * 100,
        last: { x: e.clientX, t: e.timeStamp },
        els: new Map([[id, clipEl]]),
        next: null,
      }
      setSelected(id)
      trackMarketingDemoInteracted({ demo: 'timeline', action: handle ? 'trim' : 'drag' })
    } else {
      drag.current = { kind: 'scrub' }
      trackMarketingDemoInteracted({ demo: 'timeline', action: 'scrub' })
      setSelected(null)
      setScrubbing(true)
      setPlaying(false)
      const x = round1(clamp(((e.clientX - geo.current.left) / geo.current.laneW) * 100, 0, 100))
      setPlayheadDom(x)
      setPlayhead(x)
    }
    e.currentTarget.setPointerCapture(e.pointerId)
    e.currentTarget.focus({ preventScroll: true })
    e.preventDefault()
  }

  // One move of the user's gesture: snap, ripple, paint — synchronously, straight to the DOM, so
  // the clip is where the pointer is on the very next paint. No React state is touched here.
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const pt = { x: e.clientX, y: e.clientY }
    const d = drag.current
    const g = geo.current
    if (!d || !g) return
    if (d.kind === 'scrub') {
      setPlayheadDom(round1(clamp(((pt.x - g.left) / g.laneW) * 100, 0, 100)))
      return
    }
    const { orig } = d
    const dx = ((pt.x - d.x0) / g.laneW) * 100
    // Snap only while the hand is placing, not throwing: a magnet at speed reads as stickiness.
    const dt = e.timeStamp - d.last.t
    const v = dt > 0 ? Math.abs(pt.x - d.last.x) / dt : 0
    d.last = { x: pt.x, t: e.timeStamp }
    const tol = v > SNAP_MAX_V ? 0 : d.tol
    const cs = clipsRef.current
    // Snap targets: the playhead, every whole second, every other clip's edges.
    const targets = [phRef.current, ...TICKS, ...cs.filter(c => c.id !== d.id).flatMap(c => [c.l, c.l + c.w])]
    let next: Clip
    let at: number | null = null
    if (d.kind === 'move') {
      let l = clamp(orig.l + dx, 0, 100 - orig.w)
      const a = nearest(l, targets, tol)
      const b = nearest(l + orig.w, targets, tol)
      if (a !== null && (b === null || Math.abs(a - l) <= Math.abs(b - l - orig.w))) {
        l = a
        at = a
      } else if (b !== null) {
        l = b - orig.w
        at = b
      }
      const row = clamp(Math.floor((pt.y - g.top - RULER_H) / TRACK_H), 0, 1)
      next = { ...orig, l: round1(l), track: orig.track.startsWith('V') ? TRACKS[row].id : orig.track }
    } else if (d.kind === 'trimL') {
      const max = orig.l + orig.w - MIN_W
      let l = clamp(orig.l + dx, 0, max)
      const a = nearest(l, targets, tol)
      if (a !== null && a >= 0 && a <= max) {
        l = a
        at = a
      }
      next = { ...orig, l: round1(l), w: round1(orig.l + orig.w - l) }
    } else {
      const min = orig.l + MIN_W
      let end = clamp(orig.l + orig.w + dx, min, 100)
      const a = nearest(end, targets, tol)
      if (a !== null && a >= min && a <= 100) {
        end = a
        at = a
      }
      next = { ...orig, w: round1(end - orig.l) }
    }
    const placed = place(cs, next)
    if (placed) {
      d.next = next
      for (const c of placed) {
        const mine = c.id === d.id
        if (!mine && c !== cs.find(x => x.id === c.id) && !d.els.has(c.id)) {
          abortHolder.current?.(c.id)
          const el = bodyRef.current!.querySelector<HTMLElement>(`[data-clip="${c.id}"]`)
          if (el) d.els.set(c.id, el)
        }
        const el = d.els.get(c.id)
        if (el) paint(el, c, mine ? (ROW[c.track] - ROW[orig.track]) * TRACK_H : 0, mine)
      }
    }
    guide(userGuideRef.current!, at)
  }

  const onPointerUp = () => {
    const d = drag.current
    drag.current = null
    if (!d) return
    if (d.kind === 'scrub') {
      setScrubbing(false)
      setPlayhead(phRef.current)
      return
    }
    guide(userGuideRef.current!, null)
    // Put the DOM back to what React last rendered, then commit; React re-applies the new layout
    // before paint (pointerup is a discrete event).
    const cs = clipsRef.current
    for (const [id, el] of d.els) {
      const c = cs.find(x => x.id === id)
      if (c) paint(el, c)
    }
    const { next } = d
    if (next) setClips(prev => place(prev, next) ?? prev)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ') {
      e.preventDefault()
      takeOver()
      trackMarketingDemoInteracted({ demo: 'timeline', action: 'play' })
      if (playing) setPlayhead(phRef.current)
      setPlaying(!playing)
      return
    }
    if (e.key === 'Escape') {
      setSelected(null)
      return
    }
    if (!selected || !e.key.startsWith('Arrow')) return
    e.preventDefault()
    takeOver()
    abortHolder.current?.(selected)
    setClips(cs => {
      const c = cs.find(x => x.id === selected)!
      let next = c
      if (e.key === 'ArrowLeft') next = { ...c, l: round1(clamp(c.l - NUDGE, 0, 100 - c.w)) }
      if (e.key === 'ArrowRight') next = { ...c, l: round1(clamp(c.l + NUDGE, 0, 100 - c.w)) }
      if (e.key === 'ArrowUp' && c.track === 'V1') next = { ...c, track: 'V2' }
      if (e.key === 'ArrowDown' && c.track === 'V2') next = { ...c, track: 'V1' }
      return next === c ? cs : (place(cs, next) ?? cs)
    })
  }

  const onClipFocus = useCallback((e: React.FocusEvent<HTMLButtonElement>) => {
    setSelected(e.currentTarget.dataset.clip!)
  }, [])

  // Space play: advance the playhead in real time, looping at the end of the sequence.
  useEffect(() => {
    if (!playing) return
    let raf = 0
    let last = performance.now()
    // Unrounded accumulator: a 120Hz frame step (~0.03%) would round away to nothing.
    let pos = phRef.current
    const tick = (t: number) => {
      pos = (pos + ((t - last) / 1000 / DURATION) * 100) % 100
      last = t
      setPlayheadDom(round1(pos))
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [playing, setPlayheadDom])

  // Ambient collaborators: one rAF loop runs a small tween queue per person. Each person plans
  // its next beat from its own seeded PRNG — pauses, jumps, scrubs, glides, and clip edits.
  useEffect(() => {
    if (!ambient || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const body = bodyRef.current!
    const overlay = overlayRef.current!
    const q = (sel: string) => overlay.querySelector<HTMLElement>(sel)!
    type Tween = { dur: number; t0?: number; begin?: () => void; on?: (k: number) => void; end?: () => void }
    type Person = {
      who: Who
      rng: () => number
      x: number
      q: Tween[]
      hold: { id: string; el: HTMLElement } | null
      first: boolean
      cursor: HTMLElement
      ring: HTMLElement
      guide: HTMLElement
    }
    const persons: Person[] = WHO.map((who, i) => ({
      who,
      rng: mulberry32(1000 + i * 7919),
      x: PEOPLE[who].x,
      q: [{ dur: 400 + i * 900 }], // stagger the first beats
      hold: null,
      first: true,
      cursor: q(`[data-cursor="${who}"]`),
      ring: q(`[data-ring="${who}"]`),
      guide: q(`[data-guide="${who}"]`),
    }))

    const setX = (p: Person, x: number) => {
      p.x = x
      p.cursor.style.left = `${x}%`
    }
    const hold = (ms: number): Tween => ({ dur: ms })
    const wander = (p: Person) => {
      const [a, b] = PEOPLE[p.who].home
      return round1(a + p.rng() * (b - a))
    }
    const glide = (p: Person, to: number): Tween => {
      let from = 0
      return {
        dur: 120 + Math.abs(to - p.x) * PEOPLE[p.who].pace,
        begin: () => (from = p.x),
        on: k => setX(p, from + (to - from) * easeOut(k)),
      }
    }
    // A short back-and-forth around the current spot, settling as it goes.
    const scrub = (p: Person): Tween => {
      let from = 0
      const amp = (1 + p.rng() * 3) * (p.rng() < 0.5 ? -1 : 1)
      const cycles = 1 + Math.round(p.rng() * 2)
      return {
        dur: 400 + p.rng() * 900,
        begin: () => (from = p.x),
        on: k => setX(p, from + amp * Math.sin(k * cycles * Math.PI * 2) * (1 - 0.5 * k)),
      }
    }
    const ringAt = (p: Person, c: Clip, dy = 0) => {
      const s = p.ring.style
      s.left = `${c.l}%`
      s.width = `${c.w}%`
      s.top = `${trackTop(c.track) + dy}px`
      s.opacity = '1'
    }
    const release = (p: Person) => {
      p.ring.style.opacity = '0'
      guide(p.guide, null)
      p.hold = null
    }
    const abort = (p: Person) => {
      if (p.hold) {
        const c = clipsRef.current.find(x => x.id === p.hold!.id)
        if (c) paint(p.hold.el, c)
      }
      p.q = []
      release(p)
    }
    abortHolder.current = id => persons.forEach(p => p.hold?.id === id && abort(p))
    const snapTick = (x: number, lo: number, hi: number) => {
      const t = nearest(x, TICKS, SNAP_PCT)
      return round1(t !== null && t >= lo && t <= hi ? t : x)
    }

    // Select a clip, move it into a free spot (or trim an edge), let go. Never the user's clip.
    const edit = (p: Person): boolean => {
      const all = clipsRef.current
      const d = drag.current
      const taken = new Set(persons.map(o => o.hold?.id))
      const cands = all.filter(c => c.id !== selRef.current && !(d && 'id' in d && d.id === c.id) && !taken.has(c.id))
      if (!cands.length) return false
      const c = cands[Math.floor(p.rng() * cands.length)]
      // Destinations: gaps that fit on this track (or the other video track), landing on a
      // neighbour's edge or a whole second — the same targets the user's drag snaps to.
      const dests: { l: number; track: TrackId }[] = []
      for (const track of c.track.startsWith('V') ? (['V2', 'V1'] as TrackId[]) : [c.track]) {
        const occ = all.filter(o => o.id !== c.id && o.track === track).sort((a, b) => a.l - b.l)
        let cur = 0
        for (const o of [...occ, { l: 100, w: 0 }]) {
          if (o.l - cur >= c.w) {
            dests.push({ l: cur, track }, { l: round1(o.l - c.w), track })
            for (const t of TICKS) if (t > cur && t + c.w < o.l) dests.push({ l: round1(t), track })
          }
          cur = Math.max(cur, o.l + o.w)
        }
      }
      const moves = dests.filter(x => x.track !== c.track || Math.abs(x.l - c.l) >= 2)
      let target: Clip
      if (p.rng() < 0.4 || !moves.length) {
        const same = all.filter(o => o.id !== c.id && o.track === c.track)
        const prevEnd = Math.max(0, ...same.filter(o => o.l < c.l).map(o => o.l + o.w))
        const nextStart = Math.min(100, ...same.filter(o => o.l > c.l).map(o => o.l))
        const end = c.l + c.w
        // Aim near the clip's original length so trims wander rather than shrink to nothing.
        const want = (baseRef.current.find(o => o.id === c.id)?.w ?? c.w) + p.rng() * 6 - 3
        if (p.rng() < 0.5) {
          const hi = end - MIN_W
          if (hi - prevEnd < 1) return false
          const l = snapTick(clamp(end - want, prevEnd, hi), prevEnd, hi)
          target = { ...c, l, w: round1(end - l) }
        } else {
          const lo = c.l + MIN_W
          if (nextStart - lo < 1) return false
          const e = snapTick(clamp(c.l + want, lo, nextStart), lo, nextStart)
          target = { ...c, w: round1(e - c.l) }
        }
        if (Math.abs(target.w - c.w) < 0.8) return false
      } else {
        const m = moves[Math.floor(p.rng() * moves.length)]
        target = { ...c, l: m.l, track: m.track }
      }
      const trimR = target.l === c.l && target.track === c.track
      const edgeOf = (x: Clip) => (trimR ? x.l + x.w : x.l)
      const dy = (ROW[target.track] - ROW[c.track]) * TRACK_H
      let snapped = false
      let el: HTMLElement | null = null
      p.q.push(
        glide(p, edgeOf(c)),
        {
          dur: 350 + p.rng() * 400,
          begin: () => {
            el = body.querySelector<HTMLElement>(`[data-clip="${c.id}"]`)
            if (!el) {
              p.q = []
              return
            }
            p.hold = { id: c.id, el }
            ringAt(p, c)
          },
        },
        {
          dur: 450 + Math.abs(edgeOf(target) - edgeOf(c)) * PEOPLE[p.who].pace + Math.abs(dy) * 4,
          on: k => {
            if (!el) return
            const e = easeOut(k)
            let cur: Clip = { ...c, l: c.l + (target.l - c.l) * e, w: c.w + (target.w - c.w) * e }
            // Magnetic: once inside the snap radius the clip jumps to the target and stays.
            if (!snapped && Math.abs(edgeOf(cur) - edgeOf(target)) <= SNAP_PCT) snapped = true
            if (snapped) {
              cur = { ...cur, l: target.l, w: target.w }
              guide(p.guide, edgeOf(target))
            }
            paint(el, cur, dy * e, true)
            ringAt(p, cur, dy * e)
            setX(p, edgeOf(cur))
          },
        },
        {
          dur: 300 + p.rng() * 400,
          end: () => {
            if (!el) return
            guide(p.guide, null)
            // Commit only if nothing changed underneath (the user may have moved something there).
            const now = clipsRef.current
            const mine = now.find(x => x.id === c.id)
            const clean = mine && mine.l === c.l && mine.w === c.w && mine.track === c.track && !overlaps(now, target)
            if (!clean) {
              paint(el, mine ?? c)
              return
            }
            paint(el, target, dy)
            ringAt(p, target)
            setClips(cs => cs.map(x => (x.id === c.id ? target : x)))
          },
        },
        { dur: 250 + p.rng() * 350, end: () => release(p) },
      )
      return true
    }

    const plan = (p: Person) => {
      const { calm, edits } = PEOPLE[p.who]
      // Open on an edit so the multiplayer point lands in the first seconds.
      if (p.first) {
        p.first = false
        if (edit(p)) return
      }
      const r = p.rng()
      if (r < 0.3) p.q.push(hold(250 + p.rng() * 2000 * calm))
      else if (r < 0.42) p.q.push({ dur: 0, end: () => setX(p, wander(p)) }, hold(150 + p.rng() * 500 * calm))
      else if (r < 0.58) p.q.push(scrub(p), hold(100 + p.rng() * 400 * calm))
      else if (r < 0.58 + edits && edit(p)) return
      else p.q.push(glide(p, wander(p)), hold(80 + p.rng() * 600 * calm))
    }

    let raf = 0
    // Off-screen the loop stops; on return the running tweens resume where they were.
    let pausedAt: number | null = null
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting === (pausedAt === null)) return
      if (!entry.isIntersecting) {
        cancelAnimationFrame(raf)
        pausedAt = performance.now()
        return
      }
      const dt = performance.now() - pausedAt!
      pausedAt = null
      for (const p of persons) if (p.q[0]?.t0 !== undefined) p.q[0].t0 += dt
      raf = requestAnimationFrame(loop)
    })
    const loop = (now: number) => {
      for (const p of persons) {
        if (!p.q.length) plan(p)
        const tw = p.q[0]
        if (!tw) continue
        if (tw.t0 === undefined) {
          tw.t0 = now
          tw.begin?.()
        }
        const k = tw.dur ? Math.min(1, (now - tw.t0) / tw.dur) : 1
        tw.on?.(k)
        if (k >= 1) {
          tw.end?.()
          if (p.q[0] === tw) p.q.shift()
        }
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    io.observe(body)
    return () => {
      io.disconnect()
      cancelAnimationFrame(raf)
      persons.forEach(abort)
      abortHolder.current = null
    }
  }, [ambient])

  const selectedClip = clips.find(c => c.id === selected)

  return (
    <div className="overflow-x-auto">
      <style>{CSS}</style>
      <div className="relative min-w-[720px] select-none bg-[#1f1f1f] text-white">
        {/* Sequence bar with presence, as in the editor header */}
        <div
          className={`${compact ? 'hidden' : 'flex'} h-9 items-center justify-between border-b border-[#333] px-3 text-[11px] text-neutral-400`}
        >
          <div className="flex items-center gap-3">
            <span className="font-medium text-neutral-200">spot_02</span>
            <span className="tabular-nums">1920×1080 · 24 fps</span>
            <span ref={tcRef} className="tabular-nums text-neutral-200">
              {timecode(playhead)}
            </span>
            {selectedClip && <span className="truncate text-neutral-500">{selectedClip.label} selected</span>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1.5">
              {WHO.map(who => (
                <span
                  key={who}
                  className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold text-white ring-2 ring-[#1f1f1f]"
                  style={{ backgroundColor: color(who) }}
                >
                  {who === 'claude' ? (
                    // eslint-disable-next-line @next/next/no-img-element -- static brand mark
                    <img src="/marketing/logos-mono/claude.svg" alt="" className="h-3 w-3" />
                  ) : (
                    PEOPLE[who].name[0]
                  )}
                </span>
              ))}
              {interacted && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[9px] font-semibold text-black ring-2 ring-[#1f1f1f]">
                  Y
                </span>
              )}
            </div>
            <span>{interacted ? '4 editing' : '3 editing'}</span>
          </div>
        </div>

        {/* Ruler + tracks: one pointer surface. Clips are buttons inside it; everything else scrubs. */}
        <div
          ref={bodyRef}
          role="application"
          aria-label="Timeline. Drag the ruler to scrub, drag clips to move or trim them, Space plays."
          tabIndex={0}
          className={`relative outline-none ${scrubbing ? 'cursor-col-resize' : ''}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
        >
          <div className="flex border-b border-[#333]" style={{ height: RULER_H }}>
            <div className="shrink-0 border-r border-[#333]" style={{ width: HEADER_W }} />
            <div className="relative min-w-0 flex-1 cursor-col-resize touch-pan-y">
              {ticks.map(s => {
                const major = s % 5 === 0
                return (
                  <div key={s} className="absolute top-0" style={{ left: `${(s / DURATION) * 100}%` }}>
                    <div className={`w-px ${major ? 'h-2.5 bg-[#a3a3a3]' : 'h-1.5 bg-[#a3a3a3]/50'}`} />
                    {major && (
                      <span className="absolute left-1 top-2 whitespace-nowrap text-[10px] tabular-nums text-[#a3a3a3]">
                        00:{String(s).padStart(2, '0')}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {tracks.map((t, i) => (
            <div key={t.id}>
              {i === 2 && <div className="border-b border-[#333] bg-[#1a1a1a]" style={{ height: GAP_H }} />}
              <Track label={t.id} kind={t.kind}>
                {clips
                  .filter(c => c.track === t.id)
                  .map(c => {
                    const C = t.kind === 'video' ? VideoClip : AudioClip
                    return <C key={c.id} clip={c} selected={c.id === selected} onFocus={onClipFocus} />
                  })}
              </Track>
            </div>
          ))}

          {/* Overlay: collaborators (cursor line, selection ring, snap guide), the user's snap guide,
              and the playhead — all driven by direct style writes. */}
          <div
            ref={overlayRef}
            className="pointer-events-none absolute bottom-0 right-0 top-0 z-20"
            style={{ left: HEADER_W }}
          >
            {WHO.map(who => {
              const p = { ...PEOPLE[who], color: color(who) }
              return (
                <Fragment key={who}>
                  <div
                    data-cursor={who}
                    className={`absolute bottom-0 top-0 w-px opacity-70 ${ambient ? '' : 'hidden'}`}
                    style={{ backgroundColor: p.color, left: `${p.x}%` }}
                  >
                    <span
                      className="absolute left-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium leading-none text-white"
                      style={{ backgroundColor: p.color, top: p.top }}
                    >
                      {p.name}
                    </span>
                  </div>
                  <div
                    data-ring={who}
                    className="absolute rounded opacity-0 transition-opacity duration-150"
                    style={{ height: TRACK_H, boxShadow: `inset 0 0 0 1px ${p.color}` }}
                  >
                    <span
                      className="absolute right-1 top-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium leading-none text-white"
                      style={{ backgroundColor: p.color }}
                    >
                      {p.name}
                    </span>
                  </div>
                  <div
                    data-guide={who}
                    className="absolute bottom-0 top-0 w-px opacity-0"
                    style={{ backgroundColor: p.color, boxShadow: `0 0 6px ${p.color}` }}
                  />
                </Fragment>
              )
            })}
            {parked &&
              (() => {
                const c = clips.find(x => x.id === parked.clip)
                return c ? (
                  <div
                    className="absolute rounded"
                    style={{
                      left: `${c.l}%`,
                      width: `${c.w}%`,
                      top: trackTop(c.track),
                      height: TRACK_H,
                      boxShadow: `inset 0 0 0 1px ${parked.color}`,
                    }}
                  >
                    <span
                      className="absolute right-1 top-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium leading-none text-white"
                      style={{ backgroundColor: parked.color }}
                    >
                      {parked.name}
                    </span>
                  </div>
                ) : null
              })()}
            <div
              ref={userGuideRef}
              className="absolute bottom-0 top-0 w-px bg-white opacity-0"
              style={{ boxShadow: '0 0 6px #fff' }}
            />
            <div
              ref={playheadRef}
              className={`absolute bottom-0 top-0 w-px bg-white ${interacted ? '' : 'prod-tl-playhead'}`}
              style={{ left: `${playhead}%` }}
            >
              <span className="absolute -left-[6px] top-0 h-0 w-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-white" />
              {(scrubbing || playing) && (
                <span
                  ref={chipRef}
                  className="absolute left-2 top-1 whitespace-nowrap rounded bg-white px-1.5 py-0.5 text-[10px] font-medium tabular-nums leading-none text-black"
                >
                  {timecode(playhead)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
